package one.healthium.med

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import one.healthium.base.launcher.LocalPlatformLauncher
import one.healthium.base.launcher.createPlatformLauncher
import one.healthium.base.preferences.AppPreferences
import one.healthium.data.common.serializer.ColorThemeProvider
import one.healthium.med.presentation.feature.launch.LaunchScreen
import one.healthium.med.presentation.navigation.root.RootComponent
import one.healthium.med.presentation.navigation.root.RootComponentParams
import one.healthium.med.presentation.navigation.root.RootContent
import one.healthium.ui.compose.theme.AppTheme
import one.primehealth.analytics.compose.AppEventEffect
import one.primehealth.analytics.events.Events
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ComposeApp(onCreateComponent: suspend (RootComponentParams) -> RootComponent) {
    val appLaunchViewModel = koinViewModel<AppLaunchViewModel>()
    val appPreferences = koinInject<AppPreferences>()
    val scope = rememberCoroutineScope()

    AppEventEffect {
        if (appPreferences.isFirstAppLaunch) {
            appPreferences.isFirstAppLaunch = false
            logEvent(Events.AppInitialLaunch)
        }
    }

    AppTheme {
        CompositionLocalProvider(LocalPlatformLauncher provides createPlatformLauncher()) {
            var rootComponent by remember { mutableStateOf<RootComponent?>(null) }
            val platformLauncher = LocalPlatformLauncher.current

            LaunchedEffect(Unit) {
                scope.launch(Dispatchers.Main) {
                    appLaunchViewModel.state.collect { appLaunchState ->
                        if (rootComponent == null) {
                            rootComponent = onCreateComponent(
                                RootComponentParams(
                                    platformLauncher = platformLauncher,
                                    appLaunchState = appLaunchState,
                                    appPreferences = appPreferences,
                                )
                            )
                        } else {
                            rootComponent?.onRefreshStack(appLaunchState)
                        }
                    }
                }
            }

            rootComponent?.let {
                RootContent(it)
            }
            AnimatedVisibility(
                visible = rootComponent == null,
                enter = fadeIn(),
                exit = fadeOut(),
            ) {
                LaunchScreen()
            }
        }
    }
}

class IfaColorThemeProvider : ColorThemeProvider {
    override fun get(value: String): Pair<Long, Long>? {
        return when (value) {
            //         --light--     ---dark---
            "black" -> 0xFF000000 to 0xFF000000
            "white" -> 0xFFFFFFFF to 0xFFFFFFFF
            "red" -> 0xFFFF0000 to 0xFFFF0000
            "blue" -> 0xFF0000FF to 0xFF0000FF
            "yellow" -> 0xFFFFFF00 to 0xFFFFFF00
            "clear" -> 0x00000000.toLong() to 0x00000000
            "violet" -> 0xFFDA4960 to 0xFFDA4960
            "gold" -> 0xFFEEC26D to 0xFFEEC26D
            "carbs" -> 0xFFEA6F7F to 0xFFEA6F7F
            "protein" -> 0xFF58A8F8 to 0xFF58A8F8
            "fat" -> 0xFF6686DA to 0xFF6686DA
            "pink" -> 0xFFFA5779 to 0xFFFA5779
            "gray" -> 0xFF8E8E93 to 0xFF8E8E93
            "cool" -> 0xFFFC7D60 to 0xFFFC7D60
            "green" -> 0xFF00C237 to 0xFF00C237
            "redlips" -> 0xFFD4223C to 0xFFD4223C
            "special_facebook" -> 0xFF4968AD to 0xFF4968AD
            "special_overlay" -> 0x33000000.toLong() to 0x33000000
            "status_low" -> 0xFF03D670 to 0xFF03D670
            "status_modarate" -> 0xFFFEC226 to 0xFFFEC226
            "status_high" -> 0xFFF2556F to 0xFFF2556F
            "label_primary" -> 0xFF000000 to 0xFF000000
            "label_sesquialteral" -> 0xFF404040 to 0xFF404040
            "label_secondary" -> 0xFF666666 to 0xFF666666
            "label_tertiary" -> 0xFF999999 to 0xFF999999
            "label_quaternary" -> 0xFFCCCCCC to 0xFFCCCCCC
            "label_secondary8" -> 0xFF828282 to 0xFF828282
            "label_tertiary8" -> 0xFF575655 to 0xFF575655
            "bg_base" -> 0xFFFFFFFF to 0xFFFFFFFF
            "bg_overlay" -> 0xFFF8F6F4 to 0xFFF8F6F4
            "bg_overlay_secondary" -> 0xFFF0EEEC to 0xFFF0EEEC
            "bg_overflow" -> 0xFFF8F6F4 to 0xFFF8F6F4
            "bg_overflow_secondary" -> 0xFFFCFAF8 to 0xFFFCFAF8
            "separator_primary" -> 0x0d000000.toLong() to 0x0d000000
            else -> null
        }
    }
}